import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { MarketingLinks, SignInMenu, PilotCta, MobileNav } from "@/components/MarketingNav";
import { SignOut, UploadSimple, Gauge, Users, CreditCard, GearSix, UsersThree } from "@phosphor-icons/react";

export default function Navbar({ variant = "marketing" }) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const isApp = variant === "app";

  return (
    <header className="sticky top-0 z-40 border-b border-ink-100 bg-white/80 backdrop-blur-xl">
      <div className="max-w-[1440px] mx-auto flex items-center justify-between px-6 lg:px-10 h-16">
        <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo" aria-label="SafeForm home">
          <img src="/brand/safeform-lockup-light.svg" alt="SafeForm" className="h-8 w-auto" data-testid="nav-logo-img" />
        </Link>

        {!isApp && <MarketingLinks />}

        {isApp && (
          <nav className="hidden md:flex items-center gap-1 text-sm">
            <Link to="/dashboard" data-testid="nav-dashboard"
              className={`px-3 py-1.5 border ${location.pathname === '/dashboard' ? 'border-ink-900 bg-ink-900 text-white' : 'border-transparent hover:border-ink-100'}`}>
              <span className="inline-flex items-center gap-1.5"><Gauge size={14} weight="bold" /> Dashboard</span>
            </Link>
            <Link to="/dashboard/workers" data-testid="nav-workers"
              className={`px-3 py-1.5 border ${location.pathname.startsWith('/dashboard/workers') ? 'border-ink-900 bg-ink-900 text-white' : 'border-transparent hover:border-ink-100'}`}>
              <span className="inline-flex items-center gap-1.5"><Users size={14} weight="bold" /> Workers</span>
            </Link>
            <Link to="/dashboard/upload" data-testid="nav-upload"
              className={`px-3 py-1.5 border ${location.pathname === '/dashboard/upload' || location.pathname === '/dashboard/upload-bulk' ? 'border-ink-900 bg-ink-900 text-white' : 'border-transparent hover:border-ink-100'}`}>
              <span className="inline-flex items-center gap-1.5"><UploadSimple size={14} weight="bold" /> New Analysis</span>
            </Link>
            <Link to="/dashboard/billing" data-testid="nav-billing"
              className={`px-3 py-1.5 border ${location.pathname.startsWith('/dashboard/billing') ? 'border-ink-900 bg-ink-900 text-white' : 'border-transparent hover:border-ink-100'}`}>
              <span className="inline-flex items-center gap-1.5"><CreditCard size={14} weight="bold" /> Billing</span>
            </Link>
            <Link to="/dashboard/team" data-testid="nav-team"
              className={`px-3 py-1.5 border ${location.pathname.startsWith('/dashboard/team') ? 'border-ink-900 bg-ink-900 text-white' : 'border-transparent hover:border-ink-100'}`}>
              <span className="inline-flex items-center gap-1.5"><UsersThree size={14} weight="bold" /> Team</span>
            </Link>
          </nav>
        )}

        <div className="flex items-center gap-2">
          {user ? (
            <>
              <Link to="/dashboard" className="hidden sm:block">
                <Button variant="ghost" size="sm" data-testid="nav-go-dashboard" className="rounded-none text-ink-900">Dashboard</Button>
              </Link>
              <Link to="/dashboard/settings" className="hidden md:inline-flex" aria-label="Settings" data-testid="nav-settings">
                <Button variant="ghost" size="sm" className="rounded-none text-ink-700 hover:text-ink-900 px-2">
                  <GearSix size={16} weight="bold" />
                </Button>
              </Link>
              <div className="hidden md:flex items-center gap-2 px-2 py-1 border border-ink-100" data-testid="nav-user-chip">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-ink-900 text-white text-xs flex items-center justify-center font-semibold">{(user.name || user.email || "?")[0]?.toUpperCase()}</div>
                )}
                <span className="text-xs font-mono text-ink-700 max-w-[120px] truncate">{user.name || user.email}</span>
              </div>
              <Button variant="outline" size="sm" onClick={signOut} data-testid="nav-signout" className="rounded-none border-ink-100 hover:border-ink-900">
                <SignOut size={14} className="mr-1" /> Sign out
              </Button>
            </>
          ) : (
            <>
              <SignInMenu />
              <PilotCta />
            </>
          )}
          {!isApp && <MobileNav />}
        </div>
      </div>
    </header>
  );
}
